/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { useIntl } from 'react-intl';
import { TableProps } from 'antd/lib/table';
import { DbFolderActionLog } from '~src/types/db';
import { useFoldersActionLog } from '~src/hooks/api-hooks/useFoldersActionLog';
import Pagination from 'antd/es/pagination';
import { LOG_ACTION } from './LogModal';
import { formatDate } from '~src/utils/formatDate';

interface FolderLogProps {
  state: boolean;
  before: number;
}

const TAKE_LIMIT = 12;

export const FolderLog: React.FC<FolderLogProps> = ({ state, before }) => {
  const intl = useIntl();
  const [skipLimit, setSkipLimit] = useState(0);

  const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>();

  const foldersActionLogQuery = useFoldersActionLog(
    { before: before, take: TAKE_LIMIT, skip: skipLimit * TAKE_LIMIT },
    { enabled: state }
  );

  useEffect(() => {
    if (state) {
      foldersActionLogQuery.refetch();
    }
  }, [state]);

  useEffect(() => {
    if (!totalNumberOfItems && foldersActionLogQuery.data) {
      setTotalNumberOfItems(foldersActionLogQuery.data?.totalCount);
    }
  }, [foldersActionLogQuery.data]);

  const folderActionLogColumns: TableProps<DbFolderActionLog>['columns'] = [
    {
      title: intl.formatMessage({ id: 'date-and-time' }),
      dataIndex: 'action_at',
      key: 'action_at',
      render: (action_at) => formatDate(action_at, 'long'),
      width: '20%',
    },
    {
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'folder_name',
      key: 'folder_name',
      width: '25%',
    },

    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: 'folder_path',
      key: 'folder_path',
      width: '30%',
    },

    {
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action_type',
      key: 'action',
      render: (action) =>
        Object.keys(LOG_ACTION).filter((x) => x === action).length >= 1
          ? intl.formatMessage({ id: LOG_ACTION[action] })
          : action,
      width: '10%',
    },
    {
      title: intl.formatMessage({ id: 'by' }),
      dataIndex: 'create_by_name',
      key: 'create_by_name',
      width: '15%',
    },
  ];

  const foldersActionLogSource = useMemo(
    () =>
      (foldersActionLogQuery.data?.logs || []).map((element) => ({
        ...element,
        key: element.action_id,
      })),
    [foldersActionLogQuery.data]
  );

  return (
    <div>
      <div style={{ position: 'relative', height: 'min(65vh, 600px)' }}>
        <Table<DbFolderActionLog>
          columns={folderActionLogColumns}
          dataSource={foldersActionLogSource}
          pagination={false}
          loading={foldersActionLogQuery.isLoading}
          scroll={{ y: 'min(50vh, 450px)', scrollToFirstRowOnChange: true }}
        />
      </div>
      {totalNumberOfItems && totalNumberOfItems > 0 && (
        <Pagination
          style={{ position: 'absolute', bottom: 40, right: 0, margin: '20px' }}
          total={totalNumberOfItems}
          pageSize={12}
          showSizeChanger={false}
          onChange={(page) => {
            setSkipLimit(page - 1);
          }}
        />
      )}
    </div>
  );
};
